import React, { useState, useEffect } from "react";
import {
  ConstrainedDialog as Dialog,
  ConstrainedDialogContent as DialogContent,
  ConstrainedDialogDescription as DialogDescription,
  ConstrainedDialogHeader as DialogHeader,
  ConstrainedDialogTitle as DialogTitle,
} from "@/components/ui/constrained-dialog";
import {
  ConstrainedAlertDialog as AlertDialog,
  ConstrainedAlertDialogAction as AlertDialogAction,
  ConstrainedAlertDialogCancel as AlertDialogCancel,
  ConstrainedAlertDialogContent as AlertDialogContent,
  ConstrainedAlertDialogDescription as AlertDialogDescription,
  ConstrainedAlertDialogFooter as AlertDialogFooter,
  ConstrainedAlertDialogHeader as AlertDialogHeader,
  ConstrainedAlertDialogTitle as AlertDialogTitle,
} from "@/components/ui/constrained-alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Trash2, UserPlus, Users, Mail } from "lucide-react";
import {
  Project,
  ProjectMember,
  User,
  getProjectMembers,
  addProjectMember,
  removeProjectMember,
  searchUsers,
} from "@/services/interviewService";

interface ProjectSharingModalProps {
  isOpen: boolean;
  onClose: () => void;
  project: Project | null;
  currentUserRole: "owner" | "editor" | "viewer";
}

const ProjectSharingModal: React.FC<ProjectSharingModalProps> = ({
  isOpen,
  onClose,
  project,
  currentUserRole,
}) => {
  const [members, setMembers] = useState<ProjectMember[]>([]);
  const [newMemberEmail, setNewMemberEmail] = useState("");
  const [newMemberRole, setNewMemberRole] = useState<"editor" | "viewer">(
    "editor"
  );
  const [isLoading, setIsLoading] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [showRemoveDialog, setShowRemoveDialog] = useState(false);
  const [memberToRemove, setMemberToRemove] = useState<ProjectMember | null>(
    null
  );
  const [searchResults, setSearchResults] = useState<User[]>([]);
  const [showUserSearch, setShowUserSearch] = useState(false);

  // Load project members when modal opens
  useEffect(() => {
    if (isOpen && project) {
      loadProjectMembers();
    }
  }, [isOpen, project]);

  // Clear messages after a delay
  useEffect(() => {
    if (error || success) {
      const timer = setTimeout(() => {
        setError("");
        setSuccess("");
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error, success]);

  const loadProjectMembers = async () => {
    if (!project) return;

    setIsLoading(true);
    try {
      const projectMembers = await getProjectMembers(project.id);
      setMembers(projectMembers);
    } catch (err) {
      console.error("Failed to load project members:", err);
      setError("Failed to load project members");
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddMember = async () => {
    if (!project || !newMemberEmail.trim()) return;

    setIsAdding(true);
    setError("");
    setSuccess("");

    try {
      const result = await addProjectMember(
        project.id,
        newMemberEmail.trim(),
        newMemberRole
      );

      if (result.success && result.member) {
        setMembers([...members, result.member]);
        setNewMemberEmail("");
        setNewMemberRole("editor");
        setSuccess(
          `Successfully added ${result.member.email} as ${result.member.role}`
        );
        setShowUserSearch(false);
      } else {
        setError(result.error || "Failed to add member");
      }
    } catch (err) {
      setError("Failed to add member to project");
    } finally {
      setIsAdding(false);
    }
  };

  const handleRemoveMember = async () => {
    if (!project || !memberToRemove) return;

    try {
      const result = await removeProjectMember(
        project.id,
        memberToRemove.user_id
      );

      if (result.success) {
        setMembers(members.filter((m) => m.user_id !== memberToRemove.user_id));
        setSuccess(`Removed ${memberToRemove.email} from project`);
      } else {
        setError(result.error || "Failed to remove member");
      }
    } catch (err) {
      setError("Failed to remove member from project");
    } finally {
      setShowRemoveDialog(false);
      setMemberToRemove(null);
    }
  };

  const handleUserSearch = async (query: string) => {
    setNewMemberEmail(query);

    if (query.length > 2) {
      try {
        const users = await searchUsers(query);
        setSearchResults(users);
        setShowUserSearch(users.length > 0);
      } catch (err) {
        setSearchResults([]);
        setShowUserSearch(false);
      }
    } else {
      setSearchResults([]);
      setShowUserSearch(false);
    }
  };

  const selectUser = (user: User) => {
    setNewMemberEmail(user.email);
    setShowUserSearch(false);
    setSearchResults([]);
  };

  const getRoleBadgeColor = (role: string) => {
    switch (role) {
      case "owner":
        return "bg-blue-50 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-700";
      case "editor":
        return "bg-green-50 dark:bg-green-900/50 text-green-700 dark:text-green-300 border-green-200 dark:border-green-700";
      case "viewer":
        return "bg-gray-50 dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-600";
      default:
        return "bg-gray-50 dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-600";
    }
  };

  if (!project) return null;

  const canManageMembers =
    currentUserRole === "owner" || currentUserRole === "editor";

  return (
    <>
      <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
        <DialogContent className="sm:max-w-[600px] max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
          
              Project Sharing - {project.name}
            </DialogTitle>
            <DialogDescription>
              {canManageMembers
                ? "Manage who has access to this project and their permissions."
                : "View project members. Only project owners and editors can manage sharing."}
            </DialogDescription>
          </DialogHeader>

          {/* Error/Success Messages */}
          {error && (
            <div className="px-4 py-3 text-sm text-red-800 bg-red-100 border border-red-200 rounded-md">
              {error}
            </div>
          )}
          {success && (
            <div className="px-4 py-3 text-sm text-green-800 bg-green-100 border border-green-200 rounded-md">
              {success}
            </div>
          )}

          {/* Add Member Section */}
          {canManageMembers && (
            <div className="border rounded-lg p-4 space-y-4">
              <h3 className="font-medium flex items-center gap-2">
                <UserPlus className="w-4 h-4" />
                Add New Member
              </h3>

              <div className="space-y-3">
                <div className="space-y-2 relative">
                  <Label htmlFor="email">Email Address or Name</Label>
                  <div className="relative">
                    <Mail className="w-4 h-4 absolute left-3 top-3 text-gray-400 dark:text-gray-500" />
                    <Input
                      id="email"
                      type="email"
                      value={newMemberEmail}
                      onChange={(e) => handleUserSearch(e.target.value)}
                      placeholder="Enter email address or name"
                      className="pl-10"
                    />
                  </div>

                  {/* User Search Results */}
                  {showUserSearch && searchResults.length > 0 && (
                    <div className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-md shadow-lg max-h-40 overflow-y-auto">
                      {searchResults.map((user) => (
                        <button
                          key={user.user_id}
                          onClick={() => selectUser(user)}
                          className="w-full px-3 py-2 text-left hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-2"
                        >
                          <div>
                            <div className="font-medium text-gray-900 dark:text-gray-100">
                              {user.display_name}
                            </div>
                            <div className="text-sm text-gray-500 dark:text-gray-400">
                              {user.email}
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="role">Role</Label>
                  <Select
                    value={newMemberRole}
                    onValueChange={(value: "editor" | "viewer") =>
                      setNewMemberRole(value)
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="editor">
                        Editor - Can view and modify project data
                      </SelectItem>
                      <SelectItem value="viewer">
                        Viewer - Can only view project data
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <Button
                  onClick={handleAddMember}
                  disabled={!newMemberEmail.trim() || isAdding}
                  className="w-full"
                >
                  {isAdding ? "Adding..." : "Add Member"}
                </Button>
              </div>
            </div>
          )}

          {/* Members List */}
          <div className="space-y-4">
            <h3 className="font-medium">Current Members ({members.length})</h3>

            {isLoading ? (
              <div className="text-center py-8 text-gray-500">
                Loading members...
              </div>
            ) : members.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                No members found
              </div>
            ) : (
              <div className="space-y-2">
                {members.map((member) => (
                  <div
                    key={member.user_id}
                    className="flex items-center justify-between p-3 border border-gray-200 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-gray-200 dark:bg-gray-700 rounded-full flex items-center justify-center">
                        <span className="text-sm font-medium text-gray-800 dark:text-gray-200">
                          {member.first_name?.charAt(0) ||
                            member.email.charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div>
                        <div className="font-medium">
                          {member.first_name && member.last_name
                            ? `${member.first_name} ${member.last_name}`
                            : member.email}
                        </div>
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          {member.email}
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <Badge
                        variant="outline"
                        className={getRoleBadgeColor(member.role)}
                      >
                        {member.role}
                      </Badge>

                      {canManageMembers && member.role !== "owner" && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setMemberToRemove(member);
                            setShowRemoveDialog(true);
                          }}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="flex justify-end">
            <Button variant="outline" onClick={onClose}>
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Remove Member Confirmation Dialog */}
      <AlertDialog open={showRemoveDialog} onOpenChange={setShowRemoveDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Member</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to remove {memberToRemove?.email} from this
              project? They will no longer have access to the project data.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setMemberToRemove(null)}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRemoveMember}
              className="bg-red-600 hover:bg-red-700"
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default ProjectSharingModal;
